import {
  adminDebtPaymentsApi,
  DEBT_REQUEST_STATUSES,
  type AdminDebtRequest,
  type DebtRequestStatus,
} from '@/api/admin/adminDebtPaymentsApi';
import { AdminPager } from '@/components/admin/AdminPager';
import { DebtRequestDetailDialog } from '@/components/admin/debt/DebtRequestDetailDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import {
  currencyFormatter,
  formatMovementDate,
  formatTransferDate,
} from '@/utils/movements-utils';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 20;

const STATUS_LABELS: Record<DebtRequestStatus, string> = {
  PENDING_REVIEW: 'En revisión',
  APPROVED: 'Aprobadas',
  REJECTED: 'Rechazadas',
  CANCELLED: 'Canceladas',
};

const AdminTransferenciasPage = () => {
  const [status, setStatus] = useState<DebtRequestStatus>('PENDING_REVIEW');
  const [page, setPage] = useState(0);
  const [requests, setRequests] = useState<AdminDebtRequest[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<AdminDebtRequest | null>(null);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      const result = await adminDebtPaymentsApi.list({
        status,
        page,
        size: PAGE_SIZE,
      });
      if (current !== requestId.current) return;
      setRequests(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(
        error,
        'No se pudieron cargar las transferencias',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(status, page, setPage);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Transferencias</h1>
        <p className="text-sm text-gray-500">
          Solicitudes de pago de deuda por transferencia. Aprobar acredita el
          monto en la cuenta de quien la envió.
        </p>
      </div>

      <select
        aria-label="Filtrar por estado"
        value={status}
        onChange={(event) =>
          setStatus(event.target.value as DebtRequestStatus)
        }
        className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
      >
        {/* No "all" option: the endpoint always filters by a single status. */}
        {DEBT_REQUEST_STATUSES.map((value) => (
          <option key={value} value={value}>
            {STATUS_LABELS[value]}
          </option>
        ))}
      </select>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No hay solicitudes en este estado.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Persona</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Fecha declarada</TableHead>
              <TableHead>Enviada</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell className="font-medium">
                  {request.user?.name || request.user?.email || '—'}
                </TableCell>
                <TableCell className="text-right">
                  {currencyFormatter.format(request.amount)}
                </TableCell>
                <TableCell className="text-gray-600">
                  {formatTransferDate(request.transferDate)}
                </TableCell>
                <TableCell className="text-gray-600">
                  {formatMovementDate(request.createdAt)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelected(request)}
                  >
                    {request.status === 'PENDING_REVIEW' ? 'Revisar' : 'Ver'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />

      <DebtRequestDetailDialog
        request={selected}
        onClose={() => setSelected(null)}
        onReviewed={load}
      />
    </div>
  );
};

export default AdminTransferenciasPage;
